import {t, Trans} from '@lingui/macro';
import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useToggle} from '../../../../../srcCommon/helpers/hookHelpers';
import {useLocalize} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {buildAvailableSlotsSummary} from '../../../../helpers/models/schedulingLinkAvailabilityHelpers';
import {
  isSchedulingLinkAvailable,
  isSchedulingLinkOutdated,
  schedulingLinkAdvanceNoticeOptions,
  schedulingLinkMeetingBufferOptions,
  SchedulingLinkPropertiesEnum,
  schedulingLinkStartTimeIncrements,
} from '../../../../helpers/models/schedulingLinkHelpers';
import {CalendarWeekDaysEnum} from '../../../../models/api/calendarCommonTypes';
import {CalendarVersionModel} from '../../../../models/api/calendarVersionModel';
import {DraftSchedulingLinkModel} from '../../../../models/api/schedulingLinkModel';
import {VisualParticipantModel} from '../../../../models/api/visualParticipantModel';
import {ResourceMap} from '../../../../store/appTypes';
import {createViewingNowCalendar} from '../../../../store/panels/calendars/calendarsPanelActions';
import {ViewingNowCalendar} from '../../../../store/panels/calendars/calendarsPanelState';
import {CalendarsPanelId} from '../../../../store/panels/panelStateBase';
import {Localizer} from '../../../../store/stateless/internationalizationSelectors';
import {SelectedCalendarData} from '../../editor/calendarDropdownBox';
import {CalendarDropdownBoxContainer} from '../../editor/calendarDropdownBoxContainer';
import {VisualParticipantsDropdownBox} from '../../editor/participantsEditor/visualParticipantsDropdownBox';
import {CalendarEventParticipantsList} from '../../participants/calendarEventParticipantsList';
import {
  outdatedSchedulingLinkCopy,
  schedulingLearnMoreInlineLinkButton,
  schedulingLinkPropertyTooltips,
} from '../schedulingLinkViewHelpers';
import {DoubleBookingCalendarData, DoubleBookingCalendarsDropdown} from './doubleBookingCalendarsDropdown';
import {DurationDropdownBox} from './durationDropdownBox';
import {SchedulingLinkDetailRow} from './schedulingLinkDetailRow';
import {SchedulingLinkSettingsCalendarDialogContainer} from './schedulingLinkSettingsCalendarDialogContainer';
import {SchedulingLinkSettingsSection} from './schedulingLinkSettingsSection';

/*
 * Constants.
 */

const defaultStartTimeIncrements = 15;
const dropdownBoxWidth = 340;

/*
 * Props.
 */

export interface SchedulingLinkAvailabilitySettingsProps {
  panelId: CalendarsPanelId;
  showNoSelectedTimesError?: boolean;
  draftLink: DraftSchedulingLinkModel;
  calendarVersions: ResourceMap<CalendarVersionModel>;
  additionalGuests: ReadonlyArray<VisualParticipantModel>;
  advanceNoticeMinutes?: number;
  startTimeIncrements?: number;
  meetingBufferMinutes?: number;
  organizerCalendarData: SelectedCalendarData;
  viewingNowCalendars: ReadonlyArray<ViewingNowCalendar>;
  selectedCalendarsData: ReadonlyArray<SelectedCalendarData>;
  weekStartDay: CalendarWeekDaysEnum;
  findCalendarVersionName: (calendarVersion: CalendarVersionModel) => string;
  clearNoSelectedTimesError: Handler;
  onUnselectCalendarVersion: HandlerOf<number>;
  onRemoveViewingNowCalendar: HandlerOf<ViewingNowCalendar>;
  onUpdateAdditionalGuests: HandlerOf<ReadonlyArray<VisualParticipantModel>>;
  onAddAdditionalGuests: HandlerOf<ReadonlyArray<VisualParticipantModel>>;
  onUpdateAdvanceNoticeMinutes: HandlerOf<number>;
  onUpdateStartTimeIncrements: HandlerOf<number>;
  onUpdateMeetingBufferMinutes: HandlerOf<number>;
  onUpdateCalendarVersionId: HandlerOf<number>;
}

/*
 * Style.
 */

const StyledRowsWrapperDiv = styled.div`
  display: grid;
  grid-row-gap: 20px;
  padding-bottom: 20px;
`;

const StyledDoubleBookingCalendarsWrapperDiv = styled.div`
  width: ${dropdownBoxWidth}px;
`;

const StyledVisualParticipantsDropdownBox = styled(VisualParticipantsDropdownBox)`
  width: ${dropdownBoxWidth}px;
  margin: 0;
`;

const StyledCalendarListWrapper = styled.div`
  margin-top: 10px;
`;

const StyledDropdownWrapperDiv = styled.div`
  width: 280px;
  > div {
    padding: 0px;
  }
`;

const StyledAddMeetingTimesButtonWrapperDiv = styled.div`
  display: flex;
  margin-left: -10px;
`;

const StyledAvailabilitySummaryDiv = styled.div`
  padding: 7px 0px 4px;

  display: grid;
  grid-template-columns: auto 1fr;
  grid-row-gap: 5px;
  align-items: center;
  grid-template-areas:
    'icon summary'
    'error error';
`;

const StyledIconWrapper = styled.div`
  grid-area: icon;
  height: 14px;
  width: 14px;
  margin-right: 5px;
`;

const StyledTextSummaryDiv = styled.div`
  grid-area: summary;
`;

const StyledErrorDiv = styled.div`
  grid-area: error;
  color: ${(p) => p.theme.palette.red.shade40};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
`;

/*
 * Component.
 */

export const SchedulingLinkAvailabilitySettings: FC<SchedulingLinkAvailabilitySettingsProps> = (props) => {
  const localize = useLocalize();
  const [isShowingCalendar, toggleIsShowingCalendar] = useToggle();

  const {
    viewingNowCalendars,
    selectedCalendarsData,
    onRemoveViewingNowCalendar,
    onUnselectCalendarVersion,
    findCalendarVersionName,
  } = props;

  const doubleBookingViewingNowCalendars = useMemo(
    () =>
      createDoubleBookingCalendars(
        viewingNowCalendars,
        selectedCalendarsData,
        onRemoveViewingNowCalendar,
        onUnselectCalendarVersion,
        findCalendarVersionName,
      ),
    [
      viewingNowCalendars,
      selectedCalendarsData,
      onRemoveViewingNowCalendar,
      onUnselectCalendarVersion,
      findCalendarVersionName,
    ],
  );

  return (
    <>
      <SchedulingLinkSettingsSection
        sectionSummary={
          <>
            <Trans id="schedulinglink-settings-availability-summary">
              Configure the meeting times that others can choose from when they view this scheduling link.
            </Trans>
            &nbsp;
            {schedulingLearnMoreInlineLinkButton}
          </>
        }
      >
        <StyledRowsWrapperDiv>
          {renderAvailableTimesRow(props, toggleIsShowingCalendar)}
          {renderOrganizerCalendarRow(props)}
          {renderDoubleBookingRow(props, doubleBookingViewingNowCalendars)}
          {renderGuestsRow(localize, props)}
          {renderBufferRow(props)}
          {renderAdvanceNoticeRow(props)}
          {renderStartTimeIncrementsRow(props)}
        </StyledRowsWrapperDiv>
      </SchedulingLinkSettingsSection>
      {isShowingCalendar && (
        <SchedulingLinkSettingsCalendarDialogContainer
          onRequestClose={toggleIsShowingCalendar}
          panelId={props.panelId}
        />
      )}
    </>
  );
};

function renderAvailableTimesRow(
  props: SchedulingLinkAvailabilitySettingsProps,
  toggleIsShowingCalendar: Handler,
) {
  const isLinkAvailable = isSchedulingLinkAvailable(props.draftLink);
  const onClick = () => {
    props.clearNoSelectedTimesError();
    toggleIsShowingCalendar();
  };
  const renderWarning = () => (
    <Trans id="schedulinglink-settings-noselectedtimes">
      Please add some available times to create this scheduling link.
    </Trans>
  );

  return (
    <SchedulingLinkDetailRow
      label={<Trans id="schedulinglink-settings-availabletimes">Available times</Trans>}
      renderWarning={props.showNoSelectedTimesError ? renderWarning : undefined}
    >
      {isLinkAvailable && renderAvailabilitySummary(props)}
      <StyledAddMeetingTimesButtonWrapperDiv>
        <Button size={VisualSizesEnum.MEDIUM} variant={ButtonVariantsEnum.LINK} onClick={onClick}>
          {isLinkAvailable ? (
            <Trans id="schedulinglink-settings-availability-edittimes">Edit times</Trans>
          ) : (
            <Trans id="schedulinglink-settings-availability-addtimes">+ Add available meeting times</Trans>
          )}
        </Button>
      </StyledAddMeetingTimesButtonWrapperDiv>
    </SchedulingLinkDetailRow>
  );
}

function renderAvailabilitySummary(props: SchedulingLinkAvailabilitySettingsProps) {
  const isOutdated = isSchedulingLinkOutdated(props.draftLink);
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledAvailabilitySummaryDiv>
          {!isOutdated && (
            <StyledIconWrapper>
              <Icon name="checkmarkYesActivity" color={theme.palette.green.shade40} />
            </StyledIconWrapper>
          )}
          <StyledTextSummaryDiv>
            {buildAvailableSlotsSummary(props.draftLink, props.weekStartDay)}
          </StyledTextSummaryDiv>
          {isOutdated && <StyledErrorDiv>{outdatedSchedulingLinkCopy}</StyledErrorDiv>}
        </StyledAvailabilitySummaryDiv>
      )}
    </UserThemeConsumer>
  );
}

function renderOrganizerCalendarRow(props: SchedulingLinkAvailabilitySettingsProps) {
  return (
    <SchedulingLinkDetailRow label={<Trans id="schedulinglink-settings-calendar">Calendar</Trans>}>
      <StyledDropdownWrapperDiv>
        <CalendarDropdownBoxContainer
          selectedCalendarVersion={props.organizerCalendarData.calendarVersion}
          onCalendarDataSelect={(data) => props.onUpdateCalendarVersionId(data.calendarVersion.id)}
        />
      </StyledDropdownWrapperDiv>
    </SchedulingLinkDetailRow>
  );
}

function renderDoubleBookingRow(
  props: SchedulingLinkAvailabilitySettingsProps,
  doubleBookingCalendars: ReadonlyArray<DoubleBookingCalendarData>,
) {
  return (
    <SchedulingLinkDetailRow
      label={<Trans id="schedulinglink-settings-doublebooking">Prevent double booking</Trans>}
      renderTooltip={() => schedulingLinkPropertyTooltips[SchedulingLinkPropertiesEnum.DOUBLE_BOOKING]}
    >
      <StyledDoubleBookingCalendarsWrapperDiv>
        <DoubleBookingCalendarsDropdown
          panelId={props.panelId}
          calendarVersions={props.calendarVersions}
          doubleBookingCalendars={doubleBookingCalendars}
          width={dropdownBoxWidth}
        />
      </StyledDoubleBookingCalendarsWrapperDiv>
    </SchedulingLinkDetailRow>
  );
}

function renderGuestsRow(localize: Localizer, props: SchedulingLinkAvailabilitySettingsProps) {
  const hasGuests = props.additionalGuests.length > 0;
  return (
    <SchedulingLinkDetailRow
      label={<Trans id="schedulinglink-settings-additionalguests">Additional guests</Trans>}
      renderTooltip={() => schedulingLinkPropertyTooltips[SchedulingLinkPropertiesEnum.ADDITIONAL_GUESTS]}
    >
      <StyledVisualParticipantsDropdownBox
        panelId={props.panelId}
        hasExistingAttendees={hasGuests}
        onVisualParticipantsSelect={props.onAddAdditionalGuests}
        organizerAddress={props.organizerCalendarData.calendar.organizerAddress}
        isUnsavedDraft
        placeholder={localize(t('schedulinglink-settings-additionalguests-placeholder')`Add guests`)}
        width={dropdownBoxWidth}
      />
      {hasGuests && (
        <StyledCalendarListWrapper>
          <CalendarEventParticipantsList
            panelId={props.panelId}
            visualParticipants={props.additionalGuests}
            onVisualParticipantsChange={props.onUpdateAdditionalGuests}
            shouldDisableTypeButton
          />
        </StyledCalendarListWrapper>
      )}
    </SchedulingLinkDetailRow>
  );
}

function renderBufferRow(props: SchedulingLinkAvailabilitySettingsProps) {
  return (
    <SchedulingLinkDetailRow
      label={<Trans id="schedulinglink-settings-buffer">Meeting buffer</Trans>}
      renderTooltip={() => schedulingLinkPropertyTooltips[SchedulingLinkPropertiesEnum.MEETING_BUFFER]}
    >
      <StyledDropdownWrapperDiv>
        <DurationDropdownBox
          duration={props.meetingBufferMinutes || 0}
          onDurationSelect={props.onUpdateMeetingBufferMinutes}
          options={schedulingLinkMeetingBufferOptions}
          renderNullValue={() => <Trans id="scheduling-settings-meetingbuffer-none">No meeting buffer</Trans>}
        />
      </StyledDropdownWrapperDiv>
    </SchedulingLinkDetailRow>
  );
}

function renderAdvanceNoticeRow(props: SchedulingLinkAvailabilitySettingsProps) {
  return (
    <SchedulingLinkDetailRow
      label={<Trans id="schedulinglink-settings-advancenotice">Advance notice</Trans>}
      renderTooltip={() => schedulingLinkPropertyTooltips[SchedulingLinkPropertiesEnum.ADVANCE_NOTICE]}
    >
      <StyledDropdownWrapperDiv>
        <DurationDropdownBox
          duration={props.advanceNoticeMinutes || 0}
          onDurationSelect={props.onUpdateAdvanceNoticeMinutes}
          options={schedulingLinkAdvanceNoticeOptions}
          renderNullValue={() => <Trans id="scheduling-settings-advancenotice-none">No advance notice</Trans>}
        />
      </StyledDropdownWrapperDiv>
    </SchedulingLinkDetailRow>
  );
}

function renderStartTimeIncrementsRow(props: SchedulingLinkAvailabilitySettingsProps) {
  return (
    <SchedulingLinkDetailRow
      label={<Trans id="schedulinglink-settings-startincrements">Start time increments</Trans>}
      renderTooltip={() => schedulingLinkPropertyTooltips[SchedulingLinkPropertiesEnum.START_TIME_INCREMENTS]}
    >
      <StyledDropdownWrapperDiv>
        <DurationDropdownBox
          duration={props.startTimeIncrements || defaultStartTimeIncrements}
          onDurationSelect={props.onUpdateStartTimeIncrements}
          options={schedulingLinkStartTimeIncrements}
        />
      </StyledDropdownWrapperDiv>
    </SchedulingLinkDetailRow>
  );
}

function createDoubleBookingCalendars(
  viewingNowCalendars: ReadonlyArray<ViewingNowCalendar>,
  selectedCalendarsData: ReadonlyArray<SelectedCalendarData>,
  onRemoveViewingNowCalendar: HandlerOf<ViewingNowCalendar>,
  onUnselectCalendarVersion: HandlerOf<number>,
  findCalendarVersionName: (calendarVersion: CalendarVersionModel) => string,
): ReadonlyArray<DoubleBookingCalendarData> {
  const selectedDoubleBookingCalendarVersions = selectedCalendarsData.map(
    (selected) => selected.calendarVersion,
  );
  return [
    // For each selected calendarVersion, create a viewingNowCalendar that's
    // not in redux. This is done so that we can render a selected calendarVersion
    // as a ViewingNowCalendarListItem.
    ...selectedCalendarsData.map((selected) => ({
      viewingNowCalendar: createViewingNowCalendar(
        selected.calendar.organizerAddress || '',
        selected.calendarVersion,
        viewingNowCalendars,
        selectedDoubleBookingCalendarVersions,
      ),
      onRemove: () => onUnselectCalendarVersion(selected.calendarVersion.id),
      name: findCalendarVersionName(selected.calendarVersion),
    })),
    ...viewingNowCalendars.map((viewingNowCalendar) => ({
      viewingNowCalendar,
      onRemove: onRemoveViewingNowCalendar,
    })),
  ];
}
